import React, { useEffect, useState, useContext } from 'react';
import { FunctionComponent } from 'react';
import { Field } from 'formik';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { formatNumberMoneyWithDecimals } from '@shared/utils';
import { IClaimForm } from '@src/types/claimsTracker';
import CATALOGS from '@static/constants/catalogs';
import Button from '../button';
import './index.scss';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';
import Depreciation from '../depreciation';
import { useTotalLossAdjust } from '@context/adjust/totalLoss-adjust-context';
import { TotalLossAdjustDTO } from '@context/adjust/adjust';
import { BrandsId, CountryCode } from '@src/types/common';
import { IInspectionContext, InspectionContext } from '@context/inspections';
import {
  getTotalLossCPE,
  postTotalLossCPE,
  updateClaimIsTotaLossCPE,
  updateTotalLossCPE,
} from '@shared/hooks/useStoreToCPE';

interface Props {
  claimUId: string;
  claimForm?: IClaimForm;
  disabled: boolean;
  visibleDepreciation: any;
  cpeIds?: {
    cpeClaimId: string;
    cpeDamageAnalysisId: string;
  };
}

const TotalLoss: FunctionComponent<Props> = ({
  claimUId,
  claimForm,
  disabled,
  visibleDepreciation,
  cpeIds,
}): JSX.Element => {
  const [firstRender, setFirstRender] = useState<boolean>(true);
  const [confirmApplyTotalLoss, setConfirmApplyTotalLoss] = useState<boolean>(
    false,
  );
  const [totalLossIsApply, setTotalLossIsApply] = useState<boolean>(false);
  const [totalLossRecoveryAmount, setTotalLossRecoveryAmount] = useState<
    number
  >(0);
  const [totalLossTxtDate, setTotalLossTxtDate] = useState<string>('');
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [cpeTotalLossId, setCpeTotalLossId] = useState<string>();
  const { tladjust, settingTladjust } = useTotalLossAdjust();
  const { state: claimState } = useContext<IInspectionContext>(
    InspectionContext,
  );

  const handleRadioChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    setTotalLossIsApply(value === 'true');
    visibleDepreciation(value === 'true');
    if (firstRender) {
      setFirstRender(false);
    }
  };

  const validatePartner = (): boolean => {
    const isMultiCountry = localStorage.getItem('isMultiCountry') === 'true';
    const companyId = Number(localStorage.getItem('companyId'));
    const premierId = Number(BrandsId.PREMIER);

    if (isMultiCountry) {
      return claimState.workshopsInfo?.[0]?.CompanyId === premierId;
    }

    return companyId === premierId;
  };

  const titleText = validatePartner()
    ? 'Valor recuperado:'
    : 'Valor de la pérdida:';
  const subtitleText = validatePartner()
    ? `Fecha de recuperación: ${dayjs(totalLossTxtDate).format('YYYY-MM-DD')}`
    : `Fecha de Pago: ${dayjs(totalLossTxtDate).format('YYYY-MM-DD')}`;

  // Estados para controlar si los campos perdieron el foco
  const [recoveryAmountBlurred, setRecoveryAmountBlurred] = useState<boolean>(
    false,
  );

  // Obtener la moneda y locale según el tipo de cuenta
  const getCurrencyConfig = () => {
    const isLocalCRC = claimForm?.info?.countryCode === CountryCode.CRC;
    return {
      currency: isLocalCRC ? 'CRC' : 'USD',
      locale: isLocalCRC ? 'es-CR' : 'en-US',
    };
  };

  // Función unificada para formateo condicional de números
  const formatNumberWithConditionalCommas = (
    value: number,
    isBlurred: boolean,
    includeCurrency: boolean = false,
  ): string => {
    if (value === null || value === undefined || isNaN(value)) {
      return '';
    }

    const valueStr = value.toString();
    const shouldFormat = isBlurred || valueStr.includes('.');

    if (shouldFormat) {
      const { currency, locale } = getCurrencyConfig();

      if (includeCurrency) {
        return formatNumberMoneyWithDecimals(value, currency, locale);
      }

      const formatOptions: Intl.NumberFormatOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      };

      return new Intl.NumberFormat(locale, formatOptions).format(value);
    }

    return valueStr;
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const numberValue = Number(value.replace(/[^0-9.-]+/g, ''));

    // Validar que el número sea válido
    if (isNaN(numberValue)) {
      return;
    }

    switch (event.target.name) {
      case 'recovery-amount':
        setTotalLossRecoveryAmount(numberValue);
        break;
    }
  };

  const submitTotalLoss = async (confirmSave = false): Promise<void> => {
    const recoveryAmount = totalLossIsApply ? totalLossRecoveryAmount : null;
    const totalLossDate =
      confirmSave && dayjs(totalLossTxtDate).isValid()
        ? totalLossTxtDate
        : null;
    setConfirmApplyTotalLoss(confirmSave && dayjs(totalLossTxtDate).isValid());

    await new ClaimsTrackerClient().applyTotalLoss(
      claimUId,
      totalLossIsApply,
      recoveryAmount,
      null,
      totalLossDate,
      Number(localStorage.getItem('userId')),
    );

    if (
      cpeIds &&
      recoveryAmount &&
      totalLossDate &&
      totalLossIsApply &&
      cpeTotalLossId
    ) {
      await updateTotalLossCPE(
        cpeIds.cpeClaimId,
        cpeIds.cpeDamageAnalysisId,
        cpeTotalLossId,
        recoveryAmount,
        0,
        totalLossDate,
      );
    }
  };

  const confirmTotalLoss = (confirmSave = false): void => {
    (async (): Promise<void> => {
      await submitTotalLoss(confirmSave);
      setOpenConfirm(false);

      const actualCost = tladjust?.ActualCost;

      const newTotalLossAdjust: TotalLossAdjustDTO = {
        ActualCost: actualCost,
        IsTotalLoss: totalLossIsApply,
      };

      settingTladjust(newTotalLossAdjust);
    })();
  };

  const loadData = () => {
    if (claimForm?.info?.totalLoss?.isApply) {
      setTotalLossIsApply(claimForm?.info?.totalLoss?.isApply);
      visibleDepreciation(claimForm?.info?.totalLoss?.isApply);
    }

    if (claimForm?.info?.totalLoss?.totalLossDate) {
      setTotalLossTxtDate(claimForm?.info?.totalLoss?.totalLossDate);
      setConfirmApplyTotalLoss(true);
    }

    if (claimForm?.info?.totalLoss?.recoveryAmount) {
      setTotalLossRecoveryAmount(claimForm?.info?.totalLoss?.recoveryAmount);
    }
  };
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadData();
  }, [claimForm?.info?.case]);

  useEffect(() => {
    if (!firstRender) {
      confirmTotalLoss();
    }
  }, [totalLossIsApply]);

  useEffect(() => {
    const createOrUpdateTotalLoss = async () => {
      if (cpeIds?.cpeClaimId && cpeIds.cpeDamageAnalysisId) {
        await updateClaimIsTotaLossCPE(cpeIds.cpeClaimId, totalLossIsApply);
      }

      if (cpeIds?.cpeClaimId && cpeIds.cpeDamageAnalysisId) {
        const id = await getTotalLossCPE(
          cpeIds.cpeClaimId,
          cpeIds.cpeDamageAnalysisId,
        );
        setCpeTotalLossId(id);
        if (
          !id &&
          cpeIds?.cpeClaimId &&
          cpeIds.cpeDamageAnalysisId &&
          totalLossIsApply
        ) {
          const id = await postTotalLossCPE(
            cpeIds.cpeClaimId,
            cpeIds.cpeDamageAnalysisId,
            0,
            0,
            new Date().toDateString(),
          );
          setCpeTotalLossId(id);
        }
      }
    };
    createOrUpdateTotalLoss();
  }, [totalLossIsApply, cpeIds?.cpeClaimId, cpeIds?.cpeDamageAnalysisId]);

  return (
    <>
      <Box className="total-loss">
        {confirmApplyTotalLoss && dayjs(totalLossTxtDate).isValid() ? (
          <Box>
            <p className="total-loss__data--title-result">
              {titleText}{' '}
              {(() => {
                const { currency, locale } = getCurrencyConfig();
                return formatNumberMoneyWithDecimals(
                  totalLossRecoveryAmount,
                  currency,
                  locale,
                );
              })()}
            </p>
            <p className="total-loss__data--subtitle-result">{subtitleText}</p>
          </Box>
        ) : (
          <>
            <Box className="total-loss__data">
              <Box>
                <label>Aplicar perdida total</label>
                <br />
                <FormControl>
                  <RadioGroup
                    row
                    aria-labelledby="apply-total-loss"
                    name="position"
                    defaultValue={claimForm?.info?.totalLoss?.isApply ?? false}
                    onChange={handleRadioChange}
                  >
                    <FormControlLabel
                      disabled={disabled}
                      value={true}
                      control={<Radio />}
                      label="Sí"
                      labelPlacement="start"
                    />
                    <FormControlLabel
                      disabled={disabled}
                      value={false}
                      control={<Radio />}
                      label="No"
                      labelPlacement="start"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>
            {totalLossIsApply && (
              <>
                <Box className="total-loss__data">
                  <Box className="total-loss__amount">
                    <br />
                    <Field
                      key="recovery-amount"
                      as={TextField}
                      type="text"
                      label="Valor de recuperación"
                      name="recovery-amount"
                      value={formatNumberWithConditionalCommas(
                        totalLossRecoveryAmount,
                        recoveryAmountBlurred,
                        true,
                      )}
                      onChange={handleAmountChange}
                      onBlur={() => setRecoveryAmountBlurred(true)}
                      onFocus={() => setRecoveryAmountBlurred(false)}
                    />
                  </Box>
                  <Box>
                    <br />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Fecha"
                        format={CATALOGS.getCountryFormat().dateTimeFormat}
                        value={dayjs(totalLossTxtDate)}
                        slotProps={{
                          textField: {
                            variant: 'outlined',
                            margin: 'none',
                            'aria-label': 'change date',
                          },
                        }}
                        onChange={(date: any) => {
                          if (date) {
                            const dayjsDate = dayjs(date);
                            if (dayjsDate.isValid()) {
                              setTotalLossTxtDate(
                                dayjsDate.format('YYYY-MM-DD'),
                              );
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                  <Box>
                    <br />
                    <Button
                      text="Aplicar Perdida Total"
                      type="button"
                      onClick={() => setOpenConfirm(true)}
                    />
                  </Box>
                </Box>
                <Depreciation
                  claimUId={claimUId}
                  claimNumber={claimForm?.info?.case}
                  poNumber={claimForm?.info?.poNumber}
                  totalIsured={claimForm?.info?.policy.totalInsured}
                  claimantType={claimForm?.info?.type}
                  cpeIds={cpeIds}
                  cpeTotalLossId={cpeTotalLossId}
                />
              </>
            )}
          </>
        )}
      </Box>

      <Dialog open={openConfirm}>
        <DialogTitle id="confirm-restore-title">Confirmar</DialogTitle>
        <DialogContent>
          <DialogContentText
            id="confirm-payment-modal"
            style={{ width: '400px', height: '120px' }}
          >
            <br />
            ¿Está seguro que desea aplicar los cambios de perdida total?
            <br />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            text="Cancelar"
            variant="outlined"
            color="secondary"
            onClick={() => setOpenConfirm(false)}
          />
          <Button
            text="Sí, confirmar"
            variant="contained"
            color="primary"
            onClick={confirmTotalLoss}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TotalLoss;
